import { Button } from "@mui/material";
import * as yup from "yup";
import { useFormik } from "formik";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { useDispatch } from "react-redux";
import { useLoginMutation } from "../../redux-toolkit/features/auth/authApiSlice";
import { Link, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  isErrorRedirect,
  setCredentials,
} from "../../redux-toolkit/features/auth/authSlice";
import { useAlert } from "../../hooks/useAlert";
import { ReactComponent as GoogleSvg } from "../../assets/google_icon.svg";
import { ReactComponent as MicrosoftSvg } from "../../assets/microsoft_icon.svg";
import { persistor } from "../../redux-toolkit/store";
import { getApiBaseUrl } from "../../utils/apiBaseUrl";
import { consumePostLoginRedirect } from "../../utils/postLoginRedirect";

const SSO_LOGIN_FAILED_ERROR = "Login failed. Please try again.";

const LOGIN_ERROR_MESSAGES = {
  login_failed: SSO_LOGIN_FAILED_ERROR,
  account_not_found: "We couldn't find an account with this email ID",
  sso_email_missing:
    "We couldn't find an email on this account. Please try another sign-in method.",
};

const STALE_SSO_PARAMS_KEY = "quest:pendingSsoParams";

let ssoValidateStarted = false;

const stripQueryParams = (...keys) => {
  const nextQuery = new URLSearchParams(window.location.search);
  let changed = false;
  keys.forEach((key) => {
    if (!nextQuery.has(key)) return;
    nextQuery.delete(key);
    changed = true;
  });
  if (!changed) return;
  const nextSearch = nextQuery.toString();
  window.history.replaceState(
    {},
    "",
    `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}`,
  );
};

const takeSsoCallbackParams = () => {
  const query = new URLSearchParams(window.location.search);
  const userId = query.get("id");
  const token = query.get("token");
  stripQueryParams("id", "token");
  try {
    sessionStorage.removeItem(STALE_SSO_PARAMS_KEY);
  } catch (_) {
    // Ignore quota / private-mode failures.
  }
  return userId && token ? { userId, token } : null;
};

const getNetworkAwareErrorMessage = (err) => {
  const isOffline =
    typeof navigator !== "undefined" &&
    typeof navigator.onLine === "boolean" &&
    navigator.onLine === false;

  const looksLikeNetworkFailure =
    err?.status === "FETCH_ERROR" ||
    err?.name === "TypeError" ||
    /failed to fetch|networkerror|load failed|offline/i.test(
      String(err?.error || err?.message || ""),
    );

  if (isOffline || looksLikeNetworkFailure) {
    return "No internet connection, please try again";
  }

  return (
    err?.data?.message ||
    err?.error?.data?.message ||
    err?.message ||
    "Server error."
  );
};

const validationSchema = yup.object({
  email: yup
    .string("Enter your email or employee ID")
    .trim()
    .required("E-mail / Employee ID is required"),
  password: yup.string("Enter your password").required("Password is required"),
});

export const LoginForm = (props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [login] = useLoginMutation();
  const { displayAlert } = useAlert();

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      rememberFlag: false,
    },
    validationSchema: validationSchema,
    onSubmit: () => {},
  });

  const [passwordType, setPasswordType] = useState("password");
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [ssoValidating, setSsoValidating] = useState(() => {
    const query = new URLSearchParams(window.location.search);
    return Boolean(query.get("id") && query.get("token"));
  });

  const showLoginError = useCallback(
    (message) => {
      displayAlert({
        open: true,
        message,
        type: "error",
      });
    },
    [displayAlert],
  );

  useEffect(() => {
    const loginQuery = new URLSearchParams(window.location.search);
    const alertMessage = LOGIN_ERROR_MESSAGES[loginQuery.get("errorType")];
    if (alertMessage) {
      showLoginError(alertMessage);
      dispatch(isErrorRedirect({ redirect: true }));
    }
    stripQueryParams("error", "errorType");

    const ssoParams = takeSsoCallbackParams();
    if (!ssoParams || ssoValidateStarted) return;

    ssoValidateStarted = true;
    setSsoValidating(true);

    const validateUrl = `${getApiBaseUrl()}/api/auth/sso/validate/${encodeURIComponent(ssoParams.userId)}/${encodeURIComponent(ssoParams.token)}`;

    fetch(validateUrl, { method: "POST", credentials: "include" })
      .then(async (response) => {
        if (response.ok) {
          const data = await response.json();
          dispatch(setCredentials(data.data));
          persistor.persist();
          navigate(consumePostLoginRedirect("/quest"));
          return;
        }

        let message = SSO_LOGIN_FAILED_ERROR;
        try {
          const body = await response.json();
          message = body?.message || message;
        } catch (_error) {
          // Keep the generic login-failed message when the body is not JSON.
        }
        showLoginError(message);
      })
      .catch((error) => {
        showLoginError(getNetworkAwareErrorMessage(error));
      })
      .finally(() => {
        ssoValidateStarted = false;
        setSsoValidating(false);
      });
  }, [dispatch, displayAlert, navigate, showLoginError]);

  const togglePassword = () => {
    setPasswordType((type) => (type === "password" ? "text" : "password"));
  };

  const loginHandler = async (e) => {
    e?.preventDefault();
    if (ssoValidating || !showEmailForm) return;
    formik.setTouched({ email: true, password: true }, true);
    const errors = await formik.validateForm();
    if (errors.email || errors.password) return;

    navigate("/auth");
    dispatch(isErrorRedirect({ redirect: false }));
    await login({
      id: formik.values.email,
      password: formik.values.password,
      rememberFlag: formik.values.rememberFlag || false,
    })
      .unwrap()
      .then(() => {
        props.onLoginSuccess && props.onLoginSuccess();
      })
      .catch((err) => {
        showLoginError(getNetworkAwareErrorMessage(err));
        dispatch(isErrorRedirect({ redirect: true }));
      });
  };

  const startSocialLogin = (social) => {
    if (ssoValidating) return;
    window.location.href = `${getApiBaseUrl()}/api/auth/${social}`;
  };

  return (
    <form className="login__form" onSubmit={loginHandler}>
      <div className="login__sso">
        <button
          type="button"
          className="login__ssoBtn"
          disabled={ssoValidating}
          onClick={() => startSocialLogin("google")}
        >
          <span className="login__ssoIcon">
            <GoogleSvg />
          </span>
          Sign in with Google
        </button>
        <button
          type="button"
          className="login__ssoBtn"
          disabled={ssoValidating}
          onClick={() => startSocialLogin("microsoft")}
        >
          <span className="login__ssoIcon">
            <MicrosoftSvg />
          </span>
          Sign in with Microsoft
        </button>
      </div>

      <div className="login__or">
        <span className="small__seprator"></span>
        <p>OR</p>
        <span className="small__seprator"></span>
      </div>

      {!showEmailForm ? (
        <button
          type="button"
          className="login__emailBtn"
          disabled={ssoValidating}
          onClick={() => setShowEmailForm(true)}
        >
          <MailOutlineIcon />
          Sign in with Email
        </button>
      ) : (
        <>
          <input
            type="text"
            autoComplete="username"
            autoFocus
            placeholder="E-mail"
            className={
              formik.touched.email && formik.errors.email
                ? "login__input login__invalid"
                : "login__input"
            }
            name="email"
            onChange={(e) => {
              formik.setFieldValue("email", e.target.value.replace(/\s/g, ""));
            }}
            onKeyDown={(e) => {
              if (e.key === " ") e.preventDefault();
            }}
            value={formik.values.email}
          />
          {formik.touched.email && formik.errors.email && (
            <div className="login__error">
              <ErrorOutlineIcon fontSize="small" color="error" />
              <span>{formik.errors.email}</span>
            </div>
          )}
          <div className="password__input__wrapper">
            <input
              type={passwordType}
              placeholder="Password"
              name="password"
              autoComplete="current-password"
              className={
                formik.touched.password && formik.errors.password
                  ? "login__input login__invalid"
                  : "login__input"
              }
              value={formik.values.password}
              onChange={formik.handleChange}
            />
            <i className="password__visibility__icon" onClick={togglePassword}>
              {passwordType === "password" ? (
                <VisibilityIcon />
              ) : (
                <VisibilityOffIcon />
              )}
            </i>
          </div>
          {formik.touched.password && formik.errors.password && (
            <div className="login__error">
              <ErrorOutlineIcon fontSize="small" color="error" />
              <span>{formik.errors.password}</span>
            </div>
          )}
          <div className="login__remforgot">
            <div className="login__checkbox">
              <input
                type="checkbox"
                checked={formik.values.rememberFlag}
                onChange={formik.handleChange}
                id="rememberFlag"
                name="rememberFlag"
              />
              <label htmlFor="rememberFlag">Remember me</label>
            </div>
            <Link style={{ textDecoration: "none" }} to={"/forgot-password"}>
              <p className="login__forgot">Forgot your password?</p>
            </Link>
          </div>
          <Button
            fullWidth
            type="submit"
            className="login__submit"
            disabled={ssoValidating}
            sx={{
              backgroundColor: "var(--sblue)",
              "&:hover": {
                backgroundColor: "var(--sblue)",
                filter: "brightness(1.08)",
              },
            }}
            variant="contained"
          >
            LOGIN
          </Button>
        </>
      )}

      <p className="login__legal">
        By clicking on login you agree with
        <br />
        the{" "}
        <Link className="login__legalLink" to="/privacy-policy">
          {"Privacy\u00A0Policy"}
        </Link>{" "}
        &amp;{" "}
        <Link className="login__legalLink" to="/terms-and-conditions">
          {"Terms\u00A0of\u00A0Service"}
        </Link>
      </p>
    </form>
  );
};
